import { Router } from '@angular/router';
import { MercerEditableByType, MercerSurveyDecision } from '@coin/shared/master-data/util';
import { CustomerSurvey, CustomerSurveyResult } from '@coin/shared/customer-survey/util';
import { DisplayedCurrency, PermissionResource } from '@coin/shared/core/util-enums';
import { CurrencyIsoCode, deleteCascadingFieldsIfNecessary, MercerData } from '@coin/shared/core/util-models';
import { Observable } from 'rxjs';
import { PositionEvaluationSetting } from '@coin/shared/season-mgmt/util';
import { MercerSupportPositionClassHelper } from '@coin/shared/core/util-helpers';
import { PermissionsService } from '@coin/shared/auth/feature';
import { MercerState } from '../../mercer/state/mercer.state';
import { MercerSupportState } from '../../mercer-support/state/mercer-support.state';
import { MercerDialogType } from '../enums/mercer-dialog-type.enum';
import { MercerNewHireState } from '../enums/mercer-support-new-hire-state.enum';
import { MercerSupportTreeViewItem } from '../enums/mercer-support-tree-view-item.model';
import { MercerValidationState } from '../enums/mercer-support-validation-state.enum';
import {
  MercerEvaluationMember,
  MercerEvaluationMemberDetails
} from '../models/mercer-support-evaluation-member.model';
import { MercerSupportIncumbentMember, ProposalState } from '../models/mercer-support-incumbent-member.model';
import { MeritDirectDialogType } from '../../merit/merit-shared/enums/merit-direct-dialog-type.enum';

export class MercerSupportHelper {
  public static readonly seniorManagementPositionClassStart =
    MercerSupportPositionClassHelper.seniorManagementPositionClassStart;
  public static readonly germanFullEvaluationPositionClassStart =
    MercerSupportPositionClassHelper.germanFullEvaluationPositionClassStart;
  public static readonly minPositionClass = MercerSupportPositionClassHelper.minPositionClass;
  public static readonly maxPositionClass = MercerSupportPositionClassHelper.maxPositionClass;

  public static readonly isoCodeDE = MercerSupportPositionClassHelper.isoCodeDE;

  public static groupTreeItems(treeItems: MercerSupportTreeViewItem[]): Record<string, MercerSupportTreeViewItem[]> {
    return treeItems?.reduce(
      (sections, treeItem) => ({
        ...sections,
        [treeItem.orgType]: [...(sections[treeItem.orgType] ?? []), treeItem]
      }),
      {}
    );
  }

  public static hasSpecialization(data: MercerData): boolean {
    return !!data?.family && !!data?.subFamily && !!data?.specialization;
  }

  /**
   *
   * @param dialogType
   * @param member
   * @param mercerState
   * @param mercerSupportState
   * @param permissionsService
   * @returns if the position have editable specs
   */
  public static arePositionSpecsEditable(
    dialogType: MercerDialogType,
    member: MercerEvaluationMemberDetails,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    permissionsService: PermissionsService
  ): boolean {
    if (this.isReadOnly(member, dialogType, mercerState, mercerSupportState) || this.isLockedNewHire(member)) {
      return false;
    }

    const isManagerOrHeadcount = [MercerDialogType.Manager, MercerDialogType.Headcount].includes(dialogType);
    const arePositionEditableForManagerOrHeadcount =
      (isManagerOrHeadcount
        ? !this.isPositionRequested(member) && !this.isPositionRequestedForCBAdminOrFinal(member)
        : this.arePositionSpecsEditableInSupportApp(member, permissionsService)) &&
      (dialogType === MercerDialogType.Headcount ||
        MercerSupportHelper.isValidationEditable(
          member,
          mercerState,
          mercerSupportState,
          dialogType,
          permissionsService
        ));

    return arePositionEditableForManagerOrHeadcount;
  }

  private static arePositionSpecsEditableInSupportApp(
    member: MercerEvaluationMemberDetails,
    permissionsService: PermissionsService
  ): boolean {
    return this.isHrUser(permissionsService)
      ? !this.isPositionRequestedForCBAdminOrFinal(member)
      : !this.isPositionSetToFinal(member);
  }

  public static isPositionOpenOrRejected(member: MercerEvaluationMember): boolean {
    return [MercerValidationState.Open, MercerValidationState.Rejected].includes(member?.validationState);
  }

  public static isPositionSetToFinal(member: MercerEvaluationMember): boolean {
    return member?.validationState === MercerValidationState.Accepted;
  }

  public static isPositionRequested(member: MercerEvaluationMember): boolean {
    return member?.validationState === MercerValidationState.Requested;
  }

  public static isPositionRequestedForCBAdmin(member: MercerEvaluationMember): boolean {
    return member?.validationState === MercerValidationState.RequestedCb;
  }

  public static isPositionRequestedForCBAdminOrFinal(member: MercerEvaluationMember): boolean {
    return [
      MercerValidationState.RequestedCb,
      MercerValidationState.Accepted,
      MercerValidationState.Published
    ].includes(member?.validationState);
  }

  public static hidePositionClass(member: MercerEvaluationMemberDetails): boolean {
    return member?.isPositionClassHidden;
  }

  public static isIncumbentRemovable(
    incumbent: MercerSupportIncumbentMember,
    permissionsService: PermissionsService
  ): boolean {
    return (
      this.isAdmin(permissionsService) ||
      ![ProposalState.Accepted, ProposalState.Communicated].includes(incumbent.proposalState)
    );
  }

  public static isPlacementEditable(
    dialogType: MercerDialogType,
    member: MercerEvaluationMember,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    permissionsService: PermissionsService
  ): boolean {
    return (
      (this.isAdmin(permissionsService) || member?.isPlacementEditable) &&
      this.getSeasonSettings(dialogType, mercerState, mercerSupportState)?.isPlacementSettingsEnabled &&
      !this.isReadOnly(member, dialogType, mercerState, mercerSupportState) &&
      !this.isLockedNewHire(member)
    );
  }

  public static isValidationEditable(
    member: MercerEvaluationMember,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    type: MercerDialogType,
    permissionsService: PermissionsService
  ): boolean {
    return (
      (this.isAdmin(permissionsService) || member?.isValidationEditable) &&
      !this.isReadOnly(member, type, mercerState, mercerSupportState) &&
      !this.isLockedNewHire(member)
    );
  }

  public static isCountryEditable(
    dialogType: MercerDialogType,
    member: MercerEvaluationMember,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    permissionsService: PermissionsService
  ): boolean {
    if (this.isReadOnly(member, dialogType, mercerState, mercerSupportState) || this.isLockedNewHire(member)) {
      return false;
    }
    const seasonSettings = this.getSeasonSettings(dialogType, mercerState, mercerSupportState);
    const permission = seasonSettings.positionCountryEditableBy;
    switch (permission) {
      case MercerEditableByType.Nobody:
        return false;
      case MercerEditableByType.AdminOnly:
        return dialogType === MercerDialogType.Support && this.isAdmin(permissionsService);
      case MercerEditableByType.AdminAndHr:
        return (
          dialogType === MercerDialogType.Support &&
          (this.isAdmin(permissionsService) || this.isHrUser(permissionsService))
        );
      case MercerEditableByType.All:
        return true;
    }

    return false;
  }

  /** Return true if the user is Super Admin or CB Admin */
  public static isAdmin(permissionsService: PermissionsService): boolean {
    const isCBAdmin =
      permissionsService.hasPermissions(PermissionResource.AdminApp) &&
      this.hasPositionEvaluationPermissions(permissionsService);
    return isCBAdmin || permissionsService.isSuperAdmin();
  }

  public static isCandBUserOrAdmin(permissionsService: PermissionsService): boolean {
    const isCBUser =
      permissionsService.hasPermissions(PermissionResource.CAndB) &&
      this.hasPositionEvaluationPermissions(permissionsService);
    return isCBUser || this.isAdmin(permissionsService);
  }

  public static isHrUser(permissionsService: PermissionsService): boolean {
    return (
      !permissionsService.hasPermissions(PermissionResource.AdminApp) &&
      this.hasPositionEvaluationPermissions(permissionsService)
    );
  }

  public static isNonAdmin(permissionsService: PermissionsService): boolean {
    return !this.isAdmin(permissionsService);
  }

  private static hasPositionEvaluationPermissions(permissionsService: PermissionsService): boolean {
    return permissionsService.hasPermissions(PermissionResource.PositionEvaluationHR);
  }

  public static getDisplayCurrency(
    dialogType: MercerDialogType,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    memberDetails: MercerEvaluationMemberDetails
  ): CurrencyIsoCode {
    const currency =
      dialogType === MercerDialogType.Manager ? mercerState.selectedCurrency() : mercerSupportState.selectedCurrency();
    return currency === DisplayedCurrency.EUR ? DisplayedCurrency.EUR : memberDetails?.currencyExternal;
  }

  public static isGerman(member: MercerEvaluationMember): boolean {
    return member?.countryIsoCode === this.isoCodeDE;
  }

  /**
   * we check if position is a german partial evaluation to only target german users that are not allowed to see the position class
   * but if the derived position class is >= than the senior mgmt position class, we show the position section just with the derived position class
   */
  public static isPositionSectionVisibleForGermanWithPartialEvaluation(
    memberDetails: MercerEvaluationMemberDetails,
    type: MercerDialogType,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    derivedPositionClass: number
  ): boolean {
    return (
      MercerSupportHelper.isGermanPartialEvaluationBasedOnSetting(memberDetails) &&
      derivedPositionClass >= MercerSupportHelper.seniorManagementPositionClassStart
    );
  }

  public static isFinalizationAllowedWithoutInput(
    dialogType: MercerDialogType,
    memberDetails: MercerEvaluationMember
  ): boolean {
    return (
      dialogType === MercerDialogType.Manager &&
      MercerSupportHelper.isGermanPartialEvaluationBasedOnSetting(memberDetails)
    );
  }

  public static getSeasonSettings(
    type: MercerDialogType,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState
  ): PositionEvaluationSetting {
    switch (type) {
      case MercerDialogType.Support:
        return mercerSupportState.seasonSettings();
      case MercerDialogType.Manager:
        return mercerState.seasonSettings();
      // TODO: add from endpoint once implemented: /customer/api/v1/master/position-evaluation/settings
      case MercerDialogType.Headcount:
        return { questionnaireDecision: MercerSurveyDecision.JobFamilyBasis } as PositionEvaluationSetting;
      default:
        return undefined;
    }
  }

  public static isQuestionnaireDisabledForGermanWithPartialEvaluation(
    member: MercerEvaluationMember,
    type: MercerDialogType,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState
  ): boolean {
    return (
      MercerSupportHelper.isGermanPartialEvaluationBasedOnSetting(member) &&
      MercerSupportHelper.getSeasonSettings(type, mercerState, mercerSupportState)
        ?.isQuestionnaireDisabledForGermanWithPartialEvaluation
    );
  }

  public static isReadOnly(
    member: MercerEvaluationMember,
    type: MercerDialogType,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState
  ): boolean {
    return this.isLocked(member) || this.isManagerReadOnly(type, mercerState, mercerSupportState);
  }

  public static canAddNewPositions(
    type: MercerDialogType,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    permissionsService: PermissionsService
  ): boolean {
    return (
      type === MercerDialogType.Support &&
      MercerSupportHelper.isAdmin(permissionsService) &&
      MercerSupportHelper.getSeasonSettings(type, mercerState, mercerSupportState)?.isAllowedToAddPositions
    );
  }

  public static isGermanPartialEvaluationBasedOnSetting(member: MercerEvaluationMember): boolean {
    return !!member?.isGermanPartialEvaluation;
  }

  public static getSurveyResult(survey: CustomerSurvey): CustomerSurveyResult {
    const resultQuestion = survey?.questions?.find(
      question =>
        question.isActive &&
        question.answers.find(answer => answer.linkedSurveyResultId && answer.id === question.selectedAnswerId)
    );

    if (!resultQuestion) {
      return null;
    }

    const answer = resultQuestion.answers.find(
      answer => answer.linkedSurveyResultId && answer.id === resultQuestion.selectedAnswerId
    );
    return survey?.results?.find(result => result.id === answer.linkedSurveyResultId);
  }

  private static isLocked(member: MercerEvaluationMember): boolean {
    return member?.isLocked;
  }

  public static isManagerReadOnly(
    type: MercerDialogType,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState
  ): boolean {
    return (
      this.getSeasonSettings(type, mercerState, mercerSupportState)?.isManagerAppReadOnly &&
      type === MercerDialogType.Manager
    );
  }

  public static isLockedNewHire(member: MercerEvaluationMember): boolean {
    return this.isNewHire(member) && this.getNewHireState(member) !== MercerNewHireState.Approved;
  }

  public static isNewHire(member: MercerEvaluationMember): boolean {
    return member?.isNewHire;
  }

  public static getNewHireState(member: MercerEvaluationMember): MercerNewHireState {
    return member?.newHireState;
  }

  public static canLock(
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    member: MercerEvaluationMember,
    dialogType: MercerDialogType,
    permissionsService: PermissionsService
  ): boolean {
    return (
      !this.isLockedNewHire(member) &&
      !this.isReadOnly(member, dialogType, mercerState, mercerSupportState) &&
      this.isAdmin(permissionsService) &&
      dialogType === MercerDialogType.Support
    );
  }

  public static canUnlock(
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    member: MercerEvaluationMember,
    dialogType: MercerDialogType,
    permissionsService: PermissionsService
  ): boolean {
    return (
      !this.isLockedNewHire(member) &&
      this.isReadOnly(member, dialogType, mercerState, mercerSupportState) &&
      this.isAdmin(permissionsService) &&
      dialogType === MercerDialogType.Support
    );
  }

  public static canReviewNewHire(
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    member: MercerEvaluationMember,
    type: MercerDialogType
  ): boolean {
    return (
      !this.isReadOnly(member, type, mercerState, mercerSupportState) &&
      this.isNewHire(member) &&
      this.getNewHireState(member) === MercerNewHireState.ForReview
    );
  }

  public static canPromote(
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    member: MercerEvaluationMember,
    type: MercerDialogType
  ): boolean {
    return !this.isReadOnly(member, type, mercerState, mercerSupportState) && member?.isPromotionAllowed;
  }

  public static canUndoPromotion(
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    member: MercerEvaluationMember,
    type: MercerDialogType,
    permissionsService: PermissionsService
  ) {
    if (this.isReadOnly(member, type, mercerState, mercerSupportState)) return false;

    const isPromotionOpen = member?.showWasPromoted;
    const isPromotionRunning =
      this.isPositionRequested(member) || member?.validationState === MercerValidationState.Open;
    if (!isPromotionOpen || !isPromotionRunning) return false;

    if (type === MercerDialogType.Support) {
      const isAdmin = this.isAdmin(permissionsService);
      const isCAndB = permissionsService.isCAndB();
      const isHr = this.isHrUser(permissionsService);

      return isAdmin || isCAndB || isHr;
    }

    // managers
    return true;
  }

  public static lock(mercerSupportState: MercerSupportState, memberId: string): Observable<unknown> {
    return mercerSupportState.setMercerSupportPositionLock({ evaluationMemberId: memberId, lock: true });
  }

  public static unlock(mercerSupportState: MercerSupportState, memberId: string): Observable<unknown> {
    return mercerSupportState.setMercerSupportPositionLock({ evaluationMemberId: memberId, lock: false });
  }

  public static promote(
    type: MercerDialogType,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    memberId: string
  ): Observable<unknown> {
    if (type === MercerDialogType.Support) {
      return mercerSupportState.promoteMercerSupportMember(memberId);
    }
    return mercerState.promoteMercerMember(memberId);
  }

  public static undoPromote(
    type: MercerDialogType,
    mercerState: MercerState,
    mercerSupportState: MercerSupportState,
    memberId: string
  ): Observable<unknown> {
    if (type === MercerDialogType.Support) {
      return mercerSupportState.undoPromoteMercerSupportMember(memberId);
    }
    return mercerState.undoPromoteMercerMember(memberId);
  }

  public static isTreeView(router: Router): boolean {
    return router.url.includes('tree-view');
  }

  public static canDeclineOrFinalizePositionClassBasedOnValidationState(
    member: MercerEvaluationMember,
    permissionsService: PermissionsService,
    dialogType: MercerDialogType
  ): boolean {
    if (MercerSupportHelper.isPositionRequested(member)) {
      return (
        MercerSupportHelper.isAdmin(permissionsService) ||
        (MercerSupportHelper.isHrUser(permissionsService) && dialogType === MercerDialogType.Support)
      );
    }

    if (MercerSupportHelper.isPositionRequestedForCBAdmin(member)) {
      return MercerSupportHelper.isAdmin(permissionsService);
    }

    return true;
  }

  public static isFinalizationAllowed(config: {
    member: MercerEvaluationMember;
    derivedPositionClass: number;
    managerAllowed: boolean;
    mercerState: MercerState;
    mercerSupportState: MercerSupportState;
    dialogType: MercerDialogType;
    adjustedPositionClass: number;
    mercerData: MercerData;
    surveyWasSaved: boolean;
    permissionsService: PermissionsService;
  }): boolean {
    const isQuestionnaireDisabledForGermanWithPartialEvaluation =
      this.isQuestionnaireDisabledForGermanWithPartialEvaluation(
        config.member,
        config.dialogType,
        config.mercerState,
        config.mercerSupportState
      );

    return (
      (!!config.member?.newPositionClass ||
        !!config.derivedPositionClass ||
        config.member?.isPositionClassHidden ||
        (config.member?.isGermanPartialEvaluation && this.isAdmin(config.permissionsService))) &&
      (isQuestionnaireDisabledForGermanWithPartialEvaluation
        ? !!config.mercerData?.specializationCode
        : !!config.mercerData?.jobCode) &&
      !MercerSupportHelper.isPositionSetToFinal(config.member) &&
      this.canDeclineOrFinalizePositionClassBasedOnValidationState(
        config.member,
        config.permissionsService,
        config.dialogType
      ) &&
      this.isValidationEditable(
        config.member,
        config.mercerState,
        config.mercerSupportState,
        config.dialogType,
        config.permissionsService
      ) &&
      (config?.dialogType === MercerDialogType.Support ||
        !!config.adjustedPositionClass ||
        this.isFinalizationAllowedWithoutInput(config.dialogType, config.member)) &&
      (config.managerAllowed || isQuestionnaireDisabledForGermanWithPartialEvaluation) &&
      !(this.isPositionRequestedForCBAdmin(config.member) && this.isHrUser(config.permissionsService)) &&
      !(config.dialogType === MercerDialogType.Manager && this.isPositionRequested(config.member)) &&
      config.surveyWasSaved
    );
  }

  public static finalizeButtonTooltipText(config: {
    adjustedPositionClass: number;
    derivedPositionClass: number;
    member: MercerEvaluationMemberDetails;
    isFinalizationAllowed: boolean;
  }): string {
    if (!config.isFinalizationAllowed && config.derivedPositionClass && config.adjustedPositionClass) {
      const validPc = Math.abs(config.derivedPositionClass - config.adjustedPositionClass) <= 1;
      if (!validPc) {
        return 'mercer-support.dialog.manager-invalid-pc';
      }
      if (!config.member.deviationReason) {
        return 'mercer-support.dialog.manager-valid-pc-no-reason';
      }
    }
    return '';
  }

  public static deleteCascadingFieldsIfNecessary = deleteCascadingFieldsIfNecessary;

  public static isPositionClassOutOfRange({
    newPc,
    derivedPc,
    dialogType,
    meritType
  }: {
    newPc: number;
    derivedPc: number;
    dialogType: MercerDialogType;
    meritType?: MeritDirectDialogType;
  }): boolean {
    // In case of PC is not set at all
    if (isNaN(newPc)) return false;

    // In case we found a derived position class.
    if (!isNaN(derivedPc) && derivedPc >= this.minPositionClass) {
      const isManagerDialog =
        [MercerDialogType.StandingEmployeeProfile, MercerDialogType.Manager].includes(dialogType) ||
        (dialogType === MercerDialogType.StandingMerit && meritType === MeritDirectDialogType.Allocation);
      return (
        this.minPositionClass > newPc ||
        newPc > this.maxPositionClass ||
        (isManagerDialog && Math.abs(derivedPc - newPc) > 1) // new must be +/- 1 of derived for managers
      );
    }

    // In case there was an issue finding a derived position class
    return this.minPositionClass > newPc || newPc > this.maxPositionClass;
  }
}
